import React, { useEffect, useRef, useState } from "react";

/**
 * Fixed mine-shaft siding (redesign shell), desktop ≥1080px only
 * (hidden via marketing.css media query).
 *
 * Left shaft (87px): gradient rock + grit overlay + feathered inner edge
 * (PNG alpha mask), daylight/gloom gradients, knotted rope, a miner on a
 * rope-seat that rides down with scroll, a rotated shaft label, and a live
 * depth read-out. Right shaft (34px): mirrored, no rope/miner/label.
 *
 * Scroll behaviour (verbatim from the design prototypes):
 *   p = clamp01(scrollY / (scrollHeight - clientHeight))   [passive listener]
 *   miner top     = 13% + p * 79%
 *   legs opacity  = p > 0.003 ? 1 : 0
 *   lamp glow     = p >= 0.30 ? 1 : 0
 *   depth         = round(p * 30) + " m"
 * prefers-reduced-motion: no scroll listener — the miner stays frozen at the
 * top and the flicker animation is disabled in marketing.css.
 */
export default function MineShafts() {
  const [prog, setProg] = useState(0);
  const progRef = useRef(0);

  useEffect(() => {
    if (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return undefined;
    }
    const onScroll = () => {
      const d = document.documentElement;
      const max = d.scrollHeight - d.clientHeight || 1;
      const p = Math.min(1, Math.max(0, (window.scrollY || d.scrollTop) / max));
      if (Math.abs(p - progRef.current) > 0.004) {
        progRef.current = p;
        setProg(p);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const riderTop = (13 + prog * 79).toFixed(1) + "%";
  const legsOpacity = prog > 0.003 ? 1 : 0;
  const torchOpacity = prog >= 0.3 ? 1 : 0;
  const depthLabel = Math.round(prog * 30) + " m";

  return (
    <>
      {/* ===== left shaft — hand-dug pale opal dirt ===== */}
      <div className="u-shaft" aria-hidden="true">
        <div className="u-shaft__grit" />
        <div className="u-shaft__daylight" />
        <div className="u-shaft__gloom" />
        <div className="u-shaft__rope" />

        {/* descending miner on a rope seat (tracks scroll) */}
        <div className="u-rider" style={{ top: riderTop }}>
          <div className="u-rider__glow" style={{ opacity: torchOpacity }}>
            <div className="u-rider__glow-disc" />
          </div>
          <svg viewBox="0 0 44 68" width="37" height="57" className="u-rider__svg">
            {/* rope pair + seat plank */}
            <path d="M18 -2 L12 45" stroke="#a9793c" strokeWidth="1.3" />
            <path d="M26 -2 L32 45" stroke="#a9793c" strokeWidth="1.3" />
            <rect x="8" y="44" width="28" height="4.5" rx="2" fill="#6b4a22" />
            {/* dangling legs + boots (fade in once scrolling starts) */}
            <g className="u-rider__legs" style={{ opacity: legsOpacity }}>
              <path d="M18 48 l-3.5 13" stroke="#0d0906" strokeWidth="5" strokeLinecap="round" />
              <path d="M26 48 l4 12" stroke="#0d0906" strokeWidth="5" strokeLinecap="round" />
              <path d="M13 61 l-4 1.6" stroke="#0d0906" strokeWidth="4" strokeLinecap="round" />
              <path d="M30 60 l4 1.6" stroke="#0d0906" strokeWidth="4" strokeLinecap="round" />
            </g>
            {/* torso + arms */}
            <path d="M15.5 45 L17 27 Q22 24 27 27 L28.5 45 Z" fill="#17181a" />
            <path d="M27 30 Q31 26 31 20" stroke="#17181a" strokeWidth="4.5" fill="none" strokeLinecap="round" />
            <path d="M17 31 Q13 33 12.5 36" stroke="#17181a" strokeWidth="4" fill="none" strokeLinecap="round" />
            {/* head + orange hard-hat + head-lamp */}
            <circle cx="22" cy="21.5" r="5.2" fill="#26201f" />
            <path d="M16.3 20.5 A5.7 5.7 0 0 1 27.7 20.5 Z" fill="#f15a24" />
            <rect x="14.6" y="19.6" width="14.8" height="2.6" rx="1.3" fill="#f15a24" />
            <circle cx="16.5" cy="18.6" r="2.1" fill="#ffffff" />
            {/* pick over the shoulder */}
            <line x1="12" y1="36" x2="34" y2="12" stroke="#6b4a22" strokeWidth="2" />
            <path d="M31 8 Q36 10 38 15" stroke="#d7d7d7" strokeWidth="2.2" fill="none" strokeLinecap="round" />
            <path d="M31 8 Q27.5 11 27 16" stroke="#c2c2c2" strokeWidth="2.2" fill="none" strokeLinecap="round" />
          </svg>
        </div>

        <div className="u-shaft__label">Shaft No.1 · Hand-dug · Est. 1920s</div>

        <div className="u-shaft__depth">
          <div className="u-shaft__depth-num">−{depthLabel}</div>
          <div className="u-shaft__depth-cap">DEPTH</div>
        </div>
      </div>

      {/* ===== right shaft — mirrored air shaft, thinner ===== */}
      <div className="u-shaft u-shaft-r" aria-hidden="true">
        <div className="u-shaft__grit" />
        <div className="u-shaft__daylight" />
        <div className="u-shaft__gloom" />
      </div>
    </>
  );
}
